/**
 * Episode Search Module
 * Handles transcription search and navigation
 */

import { escapeRegex } from '../core/utils.js';

// Search state
let currentSearchQuery = '';
let currentSearchMatches = [];
let currentMatchIndex = -1;

/**
 * Get current search query
 */
export function getSearchQuery() {
    return currentSearchQuery;
}

/**
 * Set search query (called when opening from search results)
 */
export function setEpisodeSearchQuery(query) {
    currentSearchQuery = query || '';
}

/**
 * Search in transcription and highlight matches
 */
export function searchInTranscription() {
    const searchInput = document.getElementById('episode-search-input');
    const query = searchInput?.value?.trim();

    if (!query) {
        clearTranscriptionSearch();
        return;
    }

    currentSearchQuery = query;
    currentSearchMatches = [];
    currentMatchIndex = -1;

    const diarizedView = document.getElementById('transcription-diarized');
    const rawView = document.getElementById('transcription-raw');

    const container = diarizedView?.style.display !== 'none' ? diarizedView : rawView;
    if (!container) return;

    const textElements = container.querySelectorAll('.segment-text, .transcription-raw');

    textElements.forEach(el => {
        const originalText = el.getAttribute('data-original-text') || el.textContent;
        el.setAttribute('data-original-text', originalText);

        const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
        if (regex.test(originalText)) {
            // Create highlighted version
            const parts = originalText.split(regex);
            el.textContent = '';
            
            parts.forEach(part => {
                if (regex.test(part)) {
                    const mark = document.createElement('mark');
                    mark.className = 'search-highlight';
                    mark.textContent = part;
                    el.appendChild(mark);
                    currentSearchMatches.push(mark);
                } else if (part) {
                    el.appendChild(document.createTextNode(part));
                }
                // Reset regex lastIndex
                regex.lastIndex = 0;
            });
        } else {
            el.textContent = originalText;
        }
    });

    updateSearchUI();

    if (currentSearchMatches.length > 0) {
        navigateMatch(1);
    }
}

/**
 * Clear search highlights
 */
export function clearTranscriptionSearch() {
    currentSearchQuery = '';
    currentSearchMatches = [];
    currentMatchIndex = -1;

    const searchInput = document.getElementById('episode-search-input');
    if (searchInput) searchInput.value = '';

    const textElements = document.querySelectorAll('[data-original-text]');
    textElements.forEach(el => {
        el.textContent = el.getAttribute('data-original-text');
    });

    updateSearchUI();
}

/**
 * Navigate between search matches
 */
export function navigateMatch(direction) {
    if (currentSearchMatches.length === 0) return;

    if (currentMatchIndex >= 0 && currentSearchMatches[currentMatchIndex]) {
        currentSearchMatches[currentMatchIndex].classList.remove('current-match');
    }

    currentMatchIndex += direction;
    if (currentMatchIndex >= currentSearchMatches.length) currentMatchIndex = 0;
    if (currentMatchIndex < 0) currentMatchIndex = currentSearchMatches.length - 1;

    const currentMatch = currentSearchMatches[currentMatchIndex];
    if (currentMatch) {
        currentMatch.classList.add('current-match');
        currentMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    updateSearchUI();
}

/**
 * Update search UI elements
 */
function updateSearchUI() {
    const countEl = document.getElementById('search-match-count');
    const prevBtn = document.getElementById('prev-match-btn');
    const nextBtn = document.getElementById('next-match-btn');
    const clearBtn = document.getElementById('clear-search-btn');

    const hasMatches = currentSearchMatches.length > 0;
    const hasQuery = currentSearchQuery.length > 0;

    if (countEl) {
        countEl.style.display = hasQuery ? 'inline' : 'none';
        countEl.textContent = hasMatches
            ? `${currentMatchIndex + 1}/${currentSearchMatches.length}`
            : 'No matches';
    }

    if (prevBtn) prevBtn.style.display = hasMatches ? 'inline-block' : 'none';
    if (nextBtn) nextBtn.style.display = hasMatches ? 'inline-block' : 'none';
    if (clearBtn) clearBtn.style.display = hasQuery ? 'inline-block' : 'none';
}

/**
 * Restore search after view refresh
 */
export function restoreSearch() {
    const searchInput = document.getElementById('episode-search-input');
    if (searchInput && currentSearchQuery) {
        searchInput.value = currentSearchQuery;
        setTimeout(() => searchInTranscription(), 100);
    }
}
